/**
 * SubscriptionsView
 *
 * Estimated MRR breakdown per salon.
 * Real source of truth (Stripe Billing) arrives in Phase 2.
 */

import React, { Component } from 'react';
import { adminService } from '../../../_services/admin.service';

const fmtGBP = (n, opts = {}) => {
    if (n == null || isNaN(n)) return '—';
    const { decimals = 2 } = opts;
    return '£' + Number(n).toLocaleString('en-GB', {
        minimumFractionDigits: decimals,
        maximumFractionDigits: decimals
    });
};
const fmtInt = (n) => (n == null || isNaN(n)) ? '—' : Number(n).toLocaleString('en-GB');

class SubscriptionsView extends Component {
    state = { loading: true, error: null, data: null, sortKey: 'mrr', sortDir: 'desc', search: '' };

    componentDidMount() { this.fetch(); }
    componentDidUpdate(prevProps) {
        if (prevProps.startDate !== this.props.startDate || prevProps.endDate !== this.props.endDate) {
            this.fetch();
        }
    }

    fetch = async () => {
        this.setState({ loading: true, error: null });
        try {
            const { startDate, endDate } = this.props;
            const res = await adminService.getFinancialsSubscriptions({ startDate, endDate });
            if (res.statusCode === 1) {
                this.setState({ data: res.responseData, loading: false });
            } else {
                this.setState({ error: res.message || 'Failed to load subscriptions', loading: false });
            }
        } catch (err) {
            console.error('financials subscriptions error', err);
            this.setState({ error: String(err), loading: false });
        }
    };

    setSort = (key) => {
        this.setState(s => ({
            sortKey: key,
            sortDir: s.sortKey === key && s.sortDir === 'desc' ? 'asc' : 'desc'
        }));
    };

    render() {
        const { loading, error, data, sortKey, sortDir, search } = this.state;
        if (loading) return <div className="fin-loading">Loading subscriptions…</div>;
        if (error) return <div className="fin-error">{error}</div>;
        if (!data) return null;

        const tiles = [
            { label: 'Estimated MRR', value: fmtGBP(data.totals.mrr, { decimals: 0 }), sub: `${fmtInt(data.totals.salonCount)} salons` },
            { label: 'Estimated ARR', value: fmtGBP(data.totals.arr, { decimals: 0 }) },
            { label: 'ARPA', value: fmtGBP(data.totals.avgArpa, { decimals: 0 }), sub: 'Avg rev per salon (subs only)' },
            { label: 'Active SEs', value: fmtInt(data.totals.seCount), sub: `${fmtGBP(data.totals.seFees, { decimals: 0 })} of MRR from SE seats` },
            { label: 'Stripe Connect cost', value: fmtGBP(data.totals.stripeConnectCost, { decimals: 0 }), sub: '£1 per SE / month' },
            { label: 'Net subscription margin', value: fmtGBP(data.totals.netMargin, { decimals: 0 }) }
        ];

        const q = search.trim().toLowerCase();
        const filtered = q
            ? data.rows.filter(r => (r.salonName || '').toLowerCase().includes(q))
            : data.rows;
        const sorted = [...filtered].sort((a, b) => {
            const av = typeof a[sortKey] === 'string' ? a[sortKey] : (a[sortKey] ?? 0);
            const bv = typeof b[sortKey] === 'string' ? b[sortKey] : (b[sortKey] ?? 0);
            if (av === bv) return 0;
            const order = av > bv ? 1 : -1;
            return sortDir === 'desc' ? -order : order;
        });

        const header = (key, label, cls = '') => (
            <th
                className={cls}
                style={{ cursor: 'pointer', userSelect: 'none' }}
                onClick={() => this.setSort(key)}
            >
                {label}
                {sortKey === key && <span style={{ marginLeft: 4 }}>{sortDir === 'desc' ? '↓' : '↑'}</span>}
            </th>
        );

        return (
            <div>
                <div className="fin-kpi-strip">
                    {tiles.map(t => (
                        <div key={t.label} className="fin-kpi-tile">
                            <div className="fin-kpi-tile-label">{t.label}</div>
                            <div className="fin-kpi-tile-value">{t.value}</div>
                            {t.sub && <div className="fin-kpi-tile-sub">{t.sub}</div>}
                        </div>
                    ))}
                </div>

                <div className="fin-section">
                    <div className="fin-section-header">
                        <h3 className="fin-section-title">Per-salon breakdown</h3>
                        <input
                            type="text"
                            placeholder="Search salon name"
                            value={search}
                            onChange={e => this.setState({ search: e.target.value })}
                            style={{
                                padding: '6px 10px',
                                border: '1px solid #e5e7eb',
                                borderRadius: 6,
                                fontSize: 13,
                                width: 200
                            }}
                        />
                    </div>
                    <table className="fin-table">
                        <thead>
                            <tr>
                                {header('salonName', 'Salon')}
                                {header('plan', 'Plan')}
                                {header('seCount', 'SEs', 'num')}
                                {header('perSeRate', '£/SE', 'num')}
                                {header('salonFee', 'Salon fee', 'num')}
                                {header('seFee', 'SE fees', 'num')}
                                {header('mrr', 'MRR', 'num')}
                                {header('stripeConnectCost', 'Stripe cost', 'num')}
                                {header('netMargin', 'Net margin', 'num')}
                            </tr>
                        </thead>
                        <tbody>
                            {sorted.map(r => (
                                <tr key={r.salonId}>
                                    <td>
                                        {r.salonName}
                                        {r.isLegacy && <span className="fin-pill warn" style={{ marginLeft: 6 }}>legacy £2/SE</span>}
                                    </td>
                                    <td>{r.plan}</td>
                                    <td className="num">{fmtInt(r.seCount)}</td>
                                    <td className="num">£{r.perSeRate}</td>
                                    <td className="num">{fmtGBP(r.salonFee)}</td>
                                    <td className="num">{fmtGBP(r.seFee)}</td>
                                    <td className="num">{fmtGBP(r.mrr)}</td>
                                    <td className="num">{fmtGBP(r.stripeConnectCost)}</td>
                                    <td className="num">{fmtGBP(r.netMargin)}</td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>

                {data.note && (
                    <p className="fin-dim" style={{ fontSize: 12, marginTop: -8 }}>{data.note}</p>
                )}
            </div>
        );
    }
}

export default SubscriptionsView;
